
/**
 * Get all url origins from the browser stack trace and get the url from the originating call with the QlikSystem
 * variable access. If the originated url is different from where this script was read from and if it is from
 * @param {*} stackTrace stack trace from a new Error object
 * @param {*} currentScript the url from where systemjs script was loaded
 * @returns 
 */
export function extractUrlsFromStackTrace(stackTrace, currentScript) {
  const systemJSUrl = currentScript;
  let systemJSExtrasAmd;
  let scriptLoadWithCredentials;
  if (systemJSUrl.indexOf("system.min.js") >= 0) {
    systemJSExtrasAmd = systemJSUrl.replace("system.min.js", "extras/amd.min.js");
    scriptLoadWithCredentials = systemJSUrl.replace("system.min.js", "extras/script-load-with-credentials.min.js");
  } else if (systemJSUrl.indexOf("system.js") >= 0) {
    systemJSExtrasAmd = systemJSUrl.replace("system.js", "extras/amd.js");
    scriptLoadWithCredentials = systemJSUrl.replace("system.js", "extras/script-load-with-credentials.js");
  } else {
    throw new Error("unknown file-name of SystemJS, it should be either system.js or system.min.js");
  }
  return Array.from(stackTrace.matchAll(/(https?:\/\/[^\s]+):[0-9]+:[0-9]+.*/g) || [])
    .map((reg) => reg && reg[1])
    .filter((f) => !f.startsWith(systemJSUrl) && !f.startsWith(systemJSExtrasAmd) && !f.startsWith(scriptLoadWithCredentials));
}

/**
 * Determines if the first url extracted from stacktrace are qlik related.
 * @param {Array<string>} urls 
 * @returns true if first url in array is qlik owned.
 */
export function isQlikOwned(urls, currentDomain) {
  return urls.length === 0 || urls[0].indexOf(currentDomain) >= 0 || urls[0].indexOf("qlikcloud.com") >= 0 || urls[0].indexOf("qlik-stage.com") >= 0;
}

/**
 * A proxy that determines which one of the loaded SystemJS that should be returned.
 * The logic is based on the origin of the invoking script that is currently being loaded
 * If it's coming from Qlik, then Qlik's version of SystemJS should be loaded,
 * otherwise the previously loaded one.
 * This code is only executed IF and only if a previously loaded version of SystemJS is detected
 * before Qlik's version is loaded.
 * @param theirs Previously loaded version of SystemJS
 * @param ours Qlik's SystemJS version
 * @returns 
 */
export function systemProxy(theirs, ours) {

  const currentScript = document.currentScript?.src || "";
  // get the origin of where the script of this systemjs was read.
  const currentDomain = currentScript.match(/https?:\/\/[^\/]*\//)?.[0];

  function getSystemInstance() {
    const urls = extractUrlsFromStackTrace(new Error().stack, currentScript);
    if (isQlikOwned(urls, currentDomain)) {
      return ours;  
    } else {
      return theirs;  
    }
  }

  return new Proxy(
    {},
    {
      get(target, prop, receiver) {
        const system = getSystemInstance();
        const p = system[prop];
        if (typeof p === 'function') {
          return p.bind(system);
        }
        return p;
        // return getSystemInstance()[prop]
      },
      set(target, prop, value, receiver) {
        getSystemInstance()[prop] = value
        return true;
      },
    }
  );
}

/**
 * Does a best guess if an object is a SystemJS instance
 * @param {*} system object to check for SystemJS features
 */
export function systemJsDetection(system) {
  return (system
     && system.resolve
     && system.import
     && system.register
     && system.instantiate
     && system.createScript
     // this is probably systemjs
  )
}
